import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import arraySupport from "dayjs/plugin/arraySupport";
import timezone from "dayjs/plugin/timezone";
import customParseFormat from "dayjs/plugin/customParseFormat";
import advancedFormat from "dayjs/plugin/advancedFormat";
import relativeTime from "dayjs/plugin/relativeTime";
import isSameOrAfter from "dayjs/plugin/isSameOrAfter";

import { useUserStore } from "../stores";

import DateFormatMapping from "../../constants/format.json";

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(customParseFormat);
dayjs.extend(advancedFormat);
dayjs.extend(relativeTime);
dayjs.extend(isSameOrAfter);
dayjs.extend(arraySupport);

function getDateFormat(locale) {
  if (!locale) {
    return DateFormatMapping["en"];
  } else if (DateFormatMapping[locale]) {
    return DateFormatMapping[locale];
  } else if (DateFormatMapping[locale.split("-")[0]]) {
    let localeID = locale.split("-")[1];
    locale = localeID && locale.split("-")[0] + "-" + localeID.toUpperCase();
    if (DateFormatMapping[locale]) {
      return DateFormatMapping[locale];
    } else {
      return DateFormatMapping[locale.split("-")[0]];
    }
  } else {
    return DateFormatMapping["en"];
  }
}

export function formatDate(date, locale) {
  if (!date) return null;
  const currentLocale = locale || window.accountDetails.DateFormat;

  let dateObj = dayjs(date);

  if (!dateObj.isValid()) {
    dateObj = dayjs(date.split(" ")[0]);
  }

  if (!dateObj.isValid()) {
    return null;
  }

  switch (currentLocale) {
    case "ISO":
      return dateObj.format(getDateFormat(currentLocale).DateFormat);
    case "US":
      return new Intl.DateTimeFormat("en-US").format(dateObj.toDate());
    case "EU":
      return new Intl.DateTimeFormat("en-150").format(dateObj.toDate());
    default:
      return dateObj.toDate().toLocaleDateString();
  }
}

export function formatDateTime(date, locale, forceSplit) {
  if (!date || typeof date !== "string") return null;
  const currentLocale =
    locale || (window.accountDetails && window.accountDetails.DateFormat);

  let dateObj = dayjs(date);

  if (!dateObj.isValid() || forceSplit) {
    let value = date && date.split(/[^0-9]/);
    dateObj = dayjs([value[0], value[1] - 1, value[2], value[3], value[4]]);
  }

  if (!dateObj.isValid()) {
    return null;
  }

  let localeId = getLocaleId(currentLocale);

  let options = {
    year: "numeric",
    month: "numeric",
    day: "numeric",
    hour: "numeric",
    minute: "numeric",
    hour12: getDateFormat(localeId).DateTimeFormat.split(" ")[2]
  };

  switch (currentLocale) {
    case "ISO":
      return dateObj.toISOString();
    case "US":
      return new Intl.DateTimeFormat("en-US", options).format(dateObj.toDate());
    case "EU":
      return new Intl.DateTimeFormat("en-150", options).format(
        dateObj.toDate()
      );
    default:
      return new Intl.DateTimeFormat(navigator.language, options).format(
        dateObj.toDate()
      );
  }
}

export function dateFormatString(locale) {
  locale = getLocaleId(locale);
  let format = getDateFormat(locale).DateFormat;
  format = format.replace(/d/g, "D").replace(/y/g, "Y");
  return format;
}

export function dateTimeFormatString(locale) {
  locale = getLocaleId(locale);
  let format = getDateFormat(locale).DateTimeFormat;
  format = format.replace(/d/g, "D").replace(/y/g, "Y");
  return format;
}

export function formatToTimeZone(dateString, locale, timeZone) {
  if (!dateString) return;
  locale = locale || window.accountDetails.DateFormat;
  let dateTimeFormat = dateTimeFormatString(locale);
  let userTimeZone = timeZone
    ? timeZone
    : Intl.DateTimeFormat().resolvedOptions().timeZone;
  let dateTimeString = dateString.split(" ")[0];
  let dateTimeObj = dayjs(dateTimeString);
  let localeId = getLocaleId(locale);

  let parsedDateTimeWithZone = new Intl.DateTimeFormat("en-Us", {
    dateStyle: "short",
    timeStyle: "short",
    hour12: getDateFormat(localeId).DateTimeFormat.split(" ")[2]
  }).format(dateTimeObj.toDate());

  return dayjs(parsedDateTimeWithZone).tz(userTimeZone).format(dateTimeFormat);
}

export function getTimeZoneAbbr(timeZone) {
  let unAbbrevated = timeZone ? dayjs().tz(timeZone).format("zzz") : null;
  if (unAbbrevated) {
    var matches = unAbbrevated.match(/\b(\w)/g);
    return matches.join("");
  }
  return null;
}

export function getTimeZoneOffset(timeZone) {
  return timeZone ? dayjs.tz(new Date(), timeZone).format("Z") : null;
}

export function getRelativeTime(date, withoutSuffix) {
  return dayjs(date).fromNow(withoutSuffix);
}

export function getFormattedDate(date, format) {
  return dayjs(date).format(format);
}

export function getFormattedUTCDate(date, format) {
  return dayjs(date).utc().format(format);
}

export function checkIsSameOrAfter(date) {
  return dayjs().isSameOrAfter(date);
}

export function getDayjsObject(date) {
  return date ? dayjs(date) : dayjs();
}

export function getDayjs() {
  return dayjs;
}

export function formatDateToUserDate(givendate, withTime) {
  const { currentUser } = useUserStore.getState();
  var timezone =
    (currentUser && currentUser.TimeZone._id) ||
    Intl.DateTimeFormat().resolvedOptions().timeZone;
  let date = new Date(
    givendate.toLocaleDateString("en-US", { timeZone: timezone })
  );
  let month = date.getMonth();
  let day = date.getDate();
  let year = date.getFullYear();
  let hours = date.getHours();
  let minutes = date.getMinutes();
  if (!withTime) {
    let dateString = `${dayjs([year, month, day]).format("YYYY-MM-DD")}`;
    return dateString;
  }
  let datetimeString = `${dayjs([year, month, day, hours, minutes])
    .tz(timezone)
    .format("YYYY-MM-DDTHH:mm:00Z")} ${timezone}`;
  return datetimeString;
}

function getLocaleId(locale) {
  if (locale === "EU") {
    locale = "en-150";
  } else if (locale === "US") {
    locale = "en-US";
  }
  if (!locale || locale === "LOCAL") {
    locale = navigator.language;
  }
  return locale;
}

export function setMonth(date, defaultDate, month, format) {
  return dayjs(date ? date : defaultDate)
    .set("month", month)
    .format(format);
}
export function setYear(date, defaultDate, year, format) {
  return dayjs(date ? date : defaultDate)
    .set("year", year)
    .format(format);
}
export function formatToUserTimeZone(dateString, isformat) {
  if (!dateString) return;
  const { currentUser } = useUserStore.getState();
  let userTimeZone =
    (currentUser && currentUser.TimeZone._id) ||
    Intl.DateTimeFormat().resolvedOptions().timeZone;
  let dateTimeString = dateString.split(" ")[0];
  let dateTimeObj = dayjs(dateTimeString);
  let parsedDateTimeWithZone = new Intl.DateTimeFormat("en-Us", {
    dateStyle: "short",
    timeStyle: "short",
    hour12: true,
    timeZone: userTimeZone
  }).format(dateTimeObj.toDate());

  return isformat
    ? parsedDateTimeWithZone
    : formatDateTime(parsedDateTimeWithZone, currentUser?.DateFormat);
}

export function isDateGreaterThanYear(date) {
  const currentUser = useUserStore.getState().currentUser;
  let userTimeZone =
    (currentUser && currentUser.TimeZone._id) ||
    Intl.DateTimeFormat().resolvedOptions().timeZone;
  let currentDateTimeOfUserZone = getTimeZoneOffset(userTimeZone);
  const inputDaysJsObj = dayjs(date).tz(userTimeZone);
  return Math.abs(inputDaysJsObj.diff(currentDateTimeOfUserZone, "days")) > 365;
}
